import type { PixelEvent } from '@/events';

export type DeadLetterReason =
  | 'max_retries_exceeded'
  | 'circuit_open'
  | 'permanent_failure'
  | 'manual';

export type DeadLetterEntry = {
  readonly id: string;
  readonly events: readonly PixelEvent[];
  readonly reason: DeadLetterReason;
  readonly failedAt: number;
  readonly attempts: number;
  readonly lastError?: string;
};

export type DeadLetterConfig = {
  readonly enabled: boolean;
  readonly maxEntries: number;
};

export type DeadLetterDebugState = {
  readonly enabled: boolean;
  readonly count: number;
  readonly maxEntries: number;
  readonly entries: readonly DeadLetterEntry[];
};

export class DeadLetterManager {
  private readonly config: DeadLetterConfig;

  private entries: DeadLetterEntry[] = [];

  constructor(config: DeadLetterConfig) {
    this.config = config;
  }

  move(
    events: readonly PixelEvent[],
    reason: DeadLetterReason,
    attempts = 0,
    lastError?: string
  ): DeadLetterEntry | null {
    if (!this.config.enabled || events.length === 0) return null;

    const entry: DeadLetterEntry = {
      id: crypto.randomUUID(),
      events: [...events],
      reason,
      failedAt: Date.now(),
      attempts,
      lastError,
    };

    this.entries.push(entry);

    if (this.entries.length > this.config.maxEntries) {
      this.entries = this.entries.slice(-this.config.maxEntries);
    }

    return entry;
  }

  replayEntry(id: string): readonly PixelEvent[] | null {
    const index = this.entries.findIndex(entry => entry.id === id);
    if (index < 0) return null;

    const [entry] = this.entries.splice(index, 1);
    return entry?.events ?? null;
  }

  replayAll(): PixelEvent[] {
    const events = this.entries.flatMap(entry => [...entry.events]);
    this.entries = [];
    return events;
  }

  clear(): void {
    this.entries = [];
  }

  getEntries(): readonly DeadLetterEntry[] {
    return [...this.entries];
  }

  getCount(): number {
    return this.entries.length;
  }

  getDebugState(): DeadLetterDebugState {
    return {
      enabled: this.config.enabled,
      count: this.entries.length,
      maxEntries: this.config.maxEntries,
      entries: this.getEntries(),
    };
  }
}
